import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, MessageSquare, ShieldCheck, Zap, Wrench, Anchor } from "lucide-react";
import { Logo } from "@/components/AppLayout";
import { HERO_IMG } from "@/lib/labels";
import { LegalLinks } from "@/pages/Legal";

const FEATURES = [
  { icon: ShieldCheck, t: "Profile with certificates", d: "Experience, turbines, skills and GWO / IRATA certificates with expiry dates in one place." },
  { icon: Briefcase, t: "Jobs from companies", d: "Service companies post onshore and offshore jobs. Apply with one click from your profile." },
  { icon: MessageSquare, t: "Messenger", d: "Build your network with invitations, send private messages, photos and PDFs." },
];

const ROLES = [{ icon: Zap, t: "Electricians" }, { icon: Wrench, t: "Mechanics" }, { icon: Anchor, t: "IRATA / rope access" }];

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <section className="relative min-h-[92vh] flex flex-col overflow-hidden grain">
        <img src={HERO_IMG} alt="View from the top of a wind turbine nacelle" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/10" />
        <header className="relative z-10 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Logo dark />
          <div className="flex items-center gap-2">
            <Link to="/sign-in" data-testid="landing-signin-button" className="px-3 py-2 text-sm font-semibold whitespace-nowrap text-white/90 hover:text-white">Sign in</Link>
            <Link to="/sign-up" data-testid="landing-signup-button-top" className="px-3 sm:px-4 py-2 text-sm font-semibold whitespace-nowrap bg-amber-500 text-slate-950 rounded hover:bg-amber-400 transition-colors">Sign up</Link>
          </div>
        </header>
        <div className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center py-16">
          <p className="eyebrow !text-amber-400 rise">Work at height · Onshore · Offshore</p>
          <h1 className="rise mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.02] max-w-3xl" style={{ animationDelay: "80ms" }}>
            The network for<br />wind turbine technicians.
          </h1>
          <p className="rise mt-6 text-base md:text-lg text-white/75 max-w-xl" style={{ animationDelay: "160ms" }}>
            Professional profile, job offers from service companies and a private messenger — for electricians, mechanics and IRATA technicians.
          </p>
          <div className="rise mt-10 flex flex-wrap gap-3" style={{ animationDelay: "240ms" }}>
            <Link to="/sign-up" data-testid="landing-signup-button" className="group inline-flex items-center gap-2 px-6 py-3.5 bg-sky-600 hover:bg-sky-500 rounded font-semibold transition-colors">
              Join for free <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/sign-up" data-testid="landing-company-button" className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/30 hover:border-white rounded font-semibold transition-colors backdrop-blur-sm">
              I'm a company — post a job
            </Link>
          </div>
          <div className="rise mt-14 flex flex-wrap gap-6" style={{ animationDelay: "320ms" }}>
            {ROLES.map((r) => (
              <span key={r.t} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70"><r.icon size={15} className="text-amber-400" />{r.t}</span>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-slate-50 text-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="eyebrow">How it works</p>
            <h2 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight">Everything you need between rotations.</h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div key={f.t} className="panel p-6">
                <span className="font-mono text-xs text-slate-400">0{i + 1}</span>
                <f.icon className="mt-4 text-sky-600" size={26} />
                <h3 className="mt-4 font-bold">{f.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="bg-slate-950 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap justify-between gap-4 text-sm text-white/50">
          <span>© {new Date().getFullYear()} WindWorkHub.com</span>
          <LegalLinks />
          <span className="font-mono text-xs">Built for those who climb.</span>
        </div>
      </footer>
    </div>
  );
}
